import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { Check } from "lucide-react";

import { ULThemeFormMessage } from "@/components/form/ULThemeFormMessage";
import { Form, FormField, FormItem } from "@/components/ui/form";
import { ULThemePasswordField } from "@/components/ULThemePasswordField";
import { cn } from "@/lib/utils";

interface PasswordConfirmationValues {
  confirmPassword: string;
}

interface PasswordConfirmationFieldProps {
  /** Current value of the primary password field, to compare against. */
  password: string;
  label?: string;
  matchText?: string;
  mismatchText?: string;
}

// Deliberately kept in its own react-hook-form instance, separate from the
// hosting screen's form, so this value is structurally impossible to include
// in the submitted signup payload — it exists purely for client-side comparison.
function PasswordConfirmationField({
  password,
  label = "Confirm password",
  matchText = "Passwords match",
  mismatchText = "Passwords do not match",
}: PasswordConfirmationFieldProps) {
  const confirmForm = useForm<PasswordConfirmationValues>({
    defaultValues: { confirmPassword: "" },
  });

  const confirmValue = confirmForm.watch("confirmPassword");

  useEffect(() => {
    if (confirmForm.formState.dirtyFields.confirmPassword) {
      confirmForm.trigger("confirmPassword");
    }
  }, [password, confirmForm]);

  const isMatch = !!confirmValue && confirmValue === password;

  return (
    <Form {...confirmForm}>
      <FormField
        control={confirmForm.control}
        name="confirmPassword"
        rules={{
          validate: (value) => !value || value === password || mismatchText,
        }}
        render={({ field, fieldState }) => (
          <FormItem>
            <ULThemePasswordField
              {...field}
              value={field.value || ""}
              label={label}
              autoComplete="new-password"
              error={!!fieldState.error}
            />
            <ULThemeFormMessage hasFormError={!!fieldState.error} />
            {isMatch && !fieldState.error && (
              <div
                className={cn(
                  "flex mb-2 items-center text-sm font-medium theme-universal:text-success"
                )}
              >
                <Check className="h-4 w-4 mr-1 flex-shrink-0" />
                {matchText}
              </div>
            )}
          </FormItem>
        )}
      />
    </Form>
  );
}

export default PasswordConfirmationField;
